<template>
    <grid-area
        area="right"
        scroll
        class="lead-detail__sidebar"
    >
        <inline-tabs selected-name="comments">
            <template #labels>
                <div class="lead-detail__sidebar__tab-labels">
                    <inline-tab-label name="comments">
                        <div class="lead-detail__sidebar__tab-label">
                            <svg-icon :icon="IconSpeakerNotes" />
                            {{ commentCollection.count }}
                        </div>
                    </inline-tab-label>

                    <inline-tab-label name="timeline">
                        <div class="lead-detail__sidebar__tab-label">
                            <svg-icon :icon="IconTimeline" />
                            {{ eventLogs.length }}
                        </div>
                    </inline-tab-label>

                    <inline-tab-label name="attachments">
                        <div class="lead-detail__sidebar__tab-label">
                            <div style="rotate: 45deg;">
                                <svg-icon :icon="IconAttachFile" />
                            </div>
                            {{ attachmentCollection.count }}
                        </div>
                    </inline-tab-label>

                    <emma-valuation-inline-tab-label
                        :lead="lead"
                    />
                </div>
            </template>

            <template #panels>
                <comments-tab-panel
                    name="comments"
                    :lead="props.lead"
                    :comment-collection="commentCollection as ReactiveCollection<Comment>"
                    :mentionable-user-collection="mentionableUserCollection.data.value"
                />
                <timeline-tab-panel
                    name="timeline"
                    :event-logs="eventLogs as EventLog[]"
                />
                <attachments-tab-panel
                    name="attachments"
                    :lead="props.lead"
                    :attachment-collection="attachmentCollection as ReactiveCollection<Attachment>"
                />
                <emma-valuation-inline-tab-panel :lead="lead" />
            </template>
        </inline-tabs>
    </grid-area>
</template>

<script lang="ts" setup>
    import { GridArea, InlineTabLabel, InlineTabs, SvgIcon } from '@keyvue/one-ui';
    import EmmaValuationInlineTabLabel from '@components/Tabs/Labels/EmmaValuationInlineTabLabel.vue';
    import EmmaValuationInlineTabPanel from '@components/Tabs/Panels/EmmaValuationInlineTabPanel.vue';
    import CommentsTabPanel from '@views/Lead/Detail/Tabs/CommentsTabPanel.vue';
    import AttachmentsTabPanel from '@views/Lead/Detail/Tabs/AttachmentsTabPanel.vue';
    import TimelineTabPanel from '@views/Lead/Detail/Tabs/TimelineTabPanel.vue';

    import IconAttachFile from '@keyvue/one-material-icons/icons/attach_file.svg?component';
    import IconSpeakerNotes from '@keyvue/one-material-icons/icons/speaker_notes.svg?component';
    import IconTimeline from '@keyvue/one-material-icons/icons/timeline.svg?component';
    import { Attachment, Lead, Comment, User } from '@keyvue/one-api-client';
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import { EventLog, EventLogAPI } from '@keyvue/one-api-client-legacy';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();
    const { getReactiveCollection, getReactiveSubCollection } = useClient();
    const eventBus = useEventBus();

    const [
        commentCollection,
        mentionableUserCollection,
        eventLogs,
        attachmentCollection,
    ] = await Promise.all([
        await getReactiveCollection(Comment, { lead: props.lead.id }),
        await getReactiveSubCollection(props.lead, User, 'mentionable-users'),
        EventLogAPI.getAll({ referral: props.lead.id }),
        await getReactiveCollection(Attachment, { lead: props.lead.id }),
    ]);

    eventBus.subscribe('resource-manipulated', async (params) => {
        if (params === 'Comment') {
            await commentCollection.load();
        }
    });

    eventBus.subscribe('resource-manipulated', async (params) => {
        if (params === 'Attachment') {
            await attachmentCollection.load();
        }
    });

    eventBus.subscribe('resource-manipulated', async (params) => {
        if (params === 'LeadPermission') {
            await mentionableUserCollection.load();
        }
    });
</script>

<style lang="scss" scoped>
.lead-detail__sidebar {
    background-color: color(brand-white);

    &__tab-labels {
        padding-top: size(sm);
        width: 100%;
        display: flex;
        justify-content: center;
    }

    &__tab-label {
        display: flex;
        align-items: center;
        gap: size(sm);
    }
}
</style>
